import { useEffect, useRef, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'root/src/client/v2/trpc/trpc';
// How often to re-read the query result while a fetch is in flight (see the comment on the effect).
const NUDGE_INTERVAL_MS = 100;
// Safety cap, so a request that never settles cannot keep nudging for the rest of the session.
const NUDGE_TIMEOUT_MS = 10000;
// The creators leaderboard (server-paginated) via the existing tRPC procedure. `keepPreviousData`
// keeps the current page while the next fetches, but when the RANKING TYPE changes we report loading
// (not the kept rows) so the previous ranking's creators never show under the new tab.
export const useLeaderboard = ({ page, pageSize, rankingType, enabled = true }) => {
    var _a, _b;
    const query = trpc.creatorDashboard.getCreatorsLeaderboard.useQuery({ currentPage: page, limit: pageSize, rankingType }, {
        enabled,
        placeholderData: keepPreviousData,
        staleTime: 30000,
        // This per-page query is a one-off, not a page-load burst, so batching only couples it to
        // unrelated requests. Sending it standalone also sidesteps a Firefox issue where a query
        // queued into a shared batch would not paint the new page until the window was refocused.
        trpc: { context: { skipBatch: true } },
    });
    const data = query.data;
    // Paint a freshly-fetched page the moment its data lands. On some browsers (reported on Firefox)
    // React Query's post-fetch notification is not flushed until the next discrete window event, so the
    // kept previous page stayed visible until the user clicked off the window. Re-reading the query
    // result while a fetch is in flight surfaces the new page anyway, and the re-render that does so is
    // what lets the effect see `isFetching` go false and stop.
    // Each wake-up re-renders the whole list (tabs, rows, avatars), so this polls every
    // NUDGE_INTERVAL_MS instead of every animation frame: imperceptible for a page swap, and ~10
    // renders a second rather than 60. rAF drives the timing so it still pauses in background tabs.
    const [, forceRerender] = useState(0);
    useEffect(() => {
        if (!query.isFetching) {
            return undefined;
        }
        let frame = 0;
        let startedAt = 0;
        let lastNudge = 0;
        const tick = (time) => {
            if (!startedAt) {
                startedAt = time;
                lastNudge = time;
            }
            if (time - startedAt > NUDGE_TIMEOUT_MS) {
                return;
            }
            if (time - lastNudge >= NUDGE_INTERVAL_MS) {
                lastNudge = time;
                forceRerender(value => value + 1);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [query.isFetching]);
    const loadedRanking = useRef(rankingType);
    useEffect(() => {
        if (!query.isPlaceholderData) {
            loadedRanking.current = rankingType;
        }
    }, [query.isPlaceholderData, rankingType]);
    const rankingIsStale = query.isPlaceholderData && loadedRanking.current !== rankingType;
    return {
        creators: ((_a = data === null || data === void 0 ? void 0 : data.creators) !== null && _a !== void 0 ? _a : []),
        pageCount: Math.max(1, (_b = data === null || data === void 0 ? void 0 : data.allPage) !== null && _b !== void 0 ? _b : 1),
        isLoading: query.isLoading || rankingIsStale,
        isFetching: query.isFetching,
        isError: query.isError,
    };
};
